import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { storyblokEditable } from '@storyblok/react';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { FirebaseCustomEvent } from '../../../analytics/firebase';
import config from '../../../config';
import ContactInquiryForm from '../../../forms/ContactInquiryForm/ContactInquiryForm';
import { sendContactInquiry } from '../../../util/api';
import { logWarning } from '../../../util/log';
import { createRecaptchaClient } from '../../../util/recaptchaV3';
import { withBaseComponent } from '../hoc/withBaseComponent';
import StoryblokRichText from './StoryblokRichText';

const DEFAULT_TOPIC = 'host';
const RECAPTCHA_ACTION = 'contact_inquiry';
const RECAPTCHA_UNAVAILABLE = 'contact-inquiry-recaptcha-unavailable';

const SUBMITTING = 'submitting';
const SUCCESS = 'success';
const ERROR = 'error';
const RECAPTCHA_ERROR = 'recaptchaError';

const ERROR_MESSAGE_IDS = {
  [ERROR]: 'ContactInquiryForm.genericError',
  [RECAPTCHA_ERROR]: 'ContactInquiryForm.recaptchaError',
};

// True when a rich-text node, or any node below it, holds visible text.
const hasRichText = (node) =>
  !!node &&
  (node.text?.trim() || (Array.isArray(node.content) && node.content.some(hasRichText)));

// An emptied rich-text field stays a doc with an empty paragraph in Storyblok; it gets no blok,
// so no empty box (plus Stack spacing) is rendered above the form or below the submit button.
const richTextBlok = (content) => (hasRichText(content) ? { general_content: content } : null);

const successTextId = (hadCallback) =>
  hadCallback ? 'ContactInquiryForm.successTextCallback' : 'ContactInquiryForm.successTextDefault';

const getContext = (intl) => ({
  currentUrl: typeof window !== 'undefined' ? window.location.href : '',
  locale: intl.locale,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
});

// Fire-and-forget: a failing analytics import must not surface as an unhandled rejection.
const trackLead = (topic) =>
  Promise.resolve(
    FirebaseCustomEvent.withCustomName('generate_lead').withParams({ topic }).logEvent()
  ).catch(() => {});

// Mostly ad/script blockers; tracked at warning level, without any form data.
const logRecaptchaUnavailable = () =>
  logWarning(new Error(RECAPTCHA_UNAVAILABLE), RECAPTCHA_UNAVAILABLE);

/**
 * Own reCAPTCHA widget in the element with `containerId`, independent of the library providers
 * elsewhere on the page (see util/recaptchaV3). Loads on mount (client only); the returned
 * function fetches a fresh token per call, as a token is single-use and expires after two
 * minutes, and resolves null when none can be obtained.
 * @param {string} containerId
 * @param {string} locale
 * @returns {(action: string) => Promise<string|null>}
 */
const useRecaptchaToken = (containerId, locale) => {
  const client = useMemo(
    () =>
      createRecaptchaClient({
        siteKey: config.google.recaptcha,
        locale,
        getContainer: () => document.getElementById(containerId),
      }),
    [containerId, locale]
  );

  useEffect(() => {
    client.preload();
  }, [client]);

  return client.getToken;
};

/**
 * Storyblok contact form block for host prospects. Posts to `/api/contact/inquiry`.
 * @param {object} props
 * @param {object} props.blok
 * @param {object} [props.baseStyles]
 */
const StoryblokContactFormComponent = ({ blok, baseStyles }) => {
  const intl = useIntl();
  const recaptchaContainerId = `recaptcha-${blok._uid}`;
  const getRecaptchaToken = useRecaptchaToken(recaptchaContainerId, intl.locale);
  const { general = {}, extra = {} } = blok;
  const {
    topic: topicField,
    headline,
    intro,
    submitLabel,
    successTitle,
    successText,
    privacyNotice,
  } = general;
  const topic = topicField || DEFAULT_TOPIC;

  const [status, setStatus] = useState('idle');
  const [hadCallback, setHadCallback] = useState(false);

  const introBlok = useMemo(() => richTextBlok(intro), [intro]);
  const privacyBlok = useMemo(() => richTextBlok(privacyNotice), [privacyNotice]);
  const privacyNode = useMemo(
    () => (privacyBlok ? <StoryblokRichText blok={privacyBlok} /> : null),
    [privacyBlok]
  );

  const handleSubmit = useCallback(
    async (values) => {
      setStatus(SUBMITTING);
      const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTION);
      if (!recaptchaToken) {
        logRecaptchaUnavailable();
        setStatus(RECAPTCHA_ERROR);
        return;
      }
      try {
        await sendContactInquiry({
          formData: { topic, ...values, recaptchaToken, ...getContext(intl) },
        });
        setHadCallback(!!values.callbackDate);
        setStatus(SUCCESS);
        trackLead(topic);
      } catch {
        setStatus(ERROR);
      }
    },
    [topic, intl, getRecaptchaToken]
  );

  const isSuccess = status === SUCCESS;
  const errorMessageId = ERROR_MESSAGE_IDS[status];

  return (
    <Box id={extra.id} {...storyblokEditable(blok)} sx={{ ...baseStyles }}>
      <Stack spacing={2}>
        {headline ? <Typography variant="h2">{headline}</Typography> : null}
        {isSuccess ? (
          <Stack spacing={1}>
            <Typography variant="h3">
              {successTitle || intl.formatMessage({ id: 'ContactInquiryForm.successTitleDefault' })}
            </Typography>
            <Typography>
              {successText || intl.formatMessage({ id: successTextId(hadCallback) })}
            </Typography>
          </Stack>
        ) : (
          <>
            {introBlok ? <StoryblokRichText blok={introBlok} /> : null}
            {errorMessageId ? (
              <Alert severity="error">{intl.formatMessage({ id: errorMessageId })}</Alert>
            ) : null}
            <ContactInquiryForm
              inProgress={status === SUBMITTING}
              onSubmit={handleSubmit}
              privacyNotice={privacyNode}
              submitLabel={submitLabel}
            />
          </>
        )}
        <Box id={recaptchaContainerId} />
      </Stack>
    </Box>
  );
};

StoryblokContactFormComponent.displayName = 'StoryblokContactForm';

const StoryblokContactForm = withBaseComponent(memo(StoryblokContactFormComponent));

export default StoryblokContactForm;
